import { Component, Inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { ImpressaoPessoaService } from '../../../../../shared/services/apis/pessoa/impressao-pessoa.service';
import { PessoaListaRequest } from '../../../../../shared/services/requests/pessoa/pessoa-lista.request';
import { PessoaImpressaoAniversarianteRequest } from '../../../../../shared/services/requests/pessoa/pessoa-impressao-aniversariante.request';
import { PessoaImpressaoStruct } from '../../../../../shared/services/structs/pessoa/pessoa-impressao.struct';
import { FoneMaskPipe } from '../../../../../shared/pipes/fone-mask.pipe';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';

export interface ImpressaoAniversariantesData {
  filtros: PessoaListaRequest;
}

@Component({
  selector: 'app-impressao-aniversariantes',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatTableModule,
    ReactiveFormsModule,
    LoadingBoxComponent,
    FoneMaskPipe,
  ],
  templateUrl: './impressao-aniversariantes.component.html',
  styleUrls: ['./impressao-aniversariantes.component.css'],
})
export class ImpressaoAniversariantesComponent implements OnInit {
  public step: 'filtro' | 'relatorio' = 'filtro';
  public formAniversariante: FormGroup;
  public isLoading: boolean = false;
  public relatorioSimples: PessoaImpressaoStruct[] = [];
  public displayedColumns: string[] = ['nome', 'cidade', 'contato', 'email', 'nascimento'];

  // Select Options
  public daysStart: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  public daysEnd: number[] = Array.from({ length: 31 }, (_, i) => i + 1);
  public months: { value: number; label: string }[] = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  constructor(
    public dialogRef: MatDialogRef<ImpressaoAniversariantesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoAniversariantesData,
    private impressaoService: ImpressaoPessoaService,
    private alertService: AlertService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initFormAniversariante();
  }

  private initFormAniversariante(): void {
    const currentMonth = new Date().getMonth() + 1;
    this.formAniversariante = this.fb.group({
      diaInicio: [1, Validators.required],
      mesInicio: [currentMonth, Validators.required],
      diaFim: [31, Validators.required],
      mesFim: [currentMonth, Validators.required],
    });

    this.daysStart = this.getDaysArrayForMonth(currentMonth);
    this.daysEnd = this.getDaysArrayForMonth(currentMonth);

    this.formAniversariante.get('mesInicio')?.valueChanges.subscribe(month => {
      this.daysStart = this.getDaysArrayForMonth(month);
      this.validateDaySelection('diaInicio', this.daysStart.length);
    });

    this.formAniversariante.get('mesFim')?.valueChanges.subscribe(month => {
      this.daysEnd = this.getDaysArrayForMonth(month);
      this.validateDaySelection('diaFim', this.daysEnd.length);
    });
  }

  private getDaysArrayForMonth(month: number): number[] {
    let daysCount = 31;
    if (month === 2) {
      daysCount = 29;
    } else if ([4, 6, 9, 11].includes(month)) {
      daysCount = 30;
    }
    return Array.from({ length: daysCount }, (_, i) => i + 1);
  }

  private validateDaySelection(controlName: string, maxDays: number): void {
    const control = this.formAniversariante.get(controlName);
    if (control && control.value > maxDays) {
      control.setValue(maxDays);
    }
  }

  public submitAniversariante(): void {
    if (this.formAniversariante.invalid) return;

    this.step = 'relatorio';
    this.isLoading = true;

    const request = new PessoaImpressaoAniversarianteRequest();
    Object.assign(request, this.data.filtros);

    request.diaInicio = this.formAniversariante.get('diaInicio')?.value;
    request.mesInicio = this.formAniversariante.get('mesInicio')?.value;
    request.diaFim = this.formAniversariante.get('diaFim')?.value;
    request.mesFim = this.formAniversariante.get('mesFim')?.value;

    // Allow modal to grow
    this.dialogRef.updateSize('90%', '90%');

    this.impressaoService.getAniversariantes(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (!res.isError) {
          this.relatorioSimples = res.listaPessoasImpressao || [];
        } else {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.step = 'filtro';
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao gerar lista de aniversariantes', AlertType.error);
        this.step = 'filtro';
      },
    });
  }

  public imprimir(): void {
    const conteudoParaImprimir = document.querySelector('.modal-print-container');

    if (conteudoParaImprimir) {
      const janelaImpressao = window.open('', '', 'height=600,width=800');

      if (janelaImpressao) {
        const styles = Array.from(document.styleSheets)
          .map(styleSheet => {
            try {
              return Array.from(styleSheet.cssRules)
                .map(rule => rule.cssText)
                .join('');
            } catch (e) {
              return '';
            }
          })
          .join('');

        janelaImpressao.document.write(`
          <html>
            <head>
              <title>Relatório de Aniversariantes</title>
              <base href="/">
              <style>
                ${styles}
                html, body {
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    margin: 0 !important;
                    font-family: Roboto, "Helvetica Neue", sans-serif;
                    color: #000;
                    background: #fff;
                }
                .no-print { display: none !important; }
                .modal-print-container {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    box-shadow: none !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .mat-mdc-table { background: white !important; table-layout: fixed !important; width: 100% !important; }
                .mat-mdc-header-cell { color: black !important; font-weight: bold !important; border-bottom: 2px solid black !important; }
                .mat-mdc-cell { color: black !important; border-bottom: 1px solid #ddd !important; }
                th, td {
                    overflow-wrap: anywhere !important;
                    word-break: break-word !important;
                    white-space: normal !important;
                    padding: 4px 6px !important;
                    font-size: 12px !important;
                }
                .table-responsive { overflow: visible !important; }
                @page { size: A4 portrait; margin: 8mm; }
                .title-header { text-align: center; margin-bottom: 20px; }
                h2 { font-size: 16px; margin: 0; }
              </style>
            </head>
            <body>
              ${conteudoParaImprimir.innerHTML}
            </body>
          </html>
        `);

        janelaImpressao.document.close();
        janelaImpressao.focus();

        setTimeout(() => {
          janelaImpressao.print();
          janelaImpressao.close();
        }, 800);
      }
    }
  }

  public voltar(): void {
    this.step = 'filtro';
    this.dialogRef.updateSize('auto', 'auto');
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
